import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Button,
  Tag,
  Empty,
  Spin,
  message,
  Modal,
  Alert,
  Tooltip,
  Input,
  Typography,
} from "antd";
import axios from "axios";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { API_CONST } from "../../../../const";

const { Text } = Typography;
const { TextArea } = Input;

const TalentDocumentsWrapper = styled.div`
  padding: 24px;
  background: #f8f9fd;
  min-height: 100vh;

  h2 {
    font-size: 24px;
    font-weight: 600;
    color: #014c75;
    margin: 0 0 24px 0;
  }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    .back-button {
      font-size: 14px;
      color: #014c75;
      &:hover { color: #00d9a9; }
    }
  }

  .talent-info {
    background: white;
    padding: 20px 24px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .info-left {
      h3 { margin: 0 0 4px 0; font-size: 20px; color: #014c75; }
      p { margin: 0; color: #666; }
    }
  }

  .verification-banner {
    border-radius: 8px;
    margin-bottom: 24px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;

    &.verified {
      background: #f6ffed;
      border: 1px solid #b7eb8f;
    }
    &.rejected {
      background: #fff2f0;
      border: 1px solid #ffccc7;
    }
    &.pending {
      background: #fffbe6;
      border: 1px solid #ffe58f;
    }

    .banner-left {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      flex: 1;

      .banner-icon { font-size: 24px; margin-top: 2px; }
      .banner-text {
        h4 { margin: 0 0 2px 0; font-size: 15px; font-weight: 600; }
        p { margin: 0; font-size: 13px; color: #555; }
      }
    }

    .banner-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
  }

  .documents-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 24px;
  }

  .document-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

    .card-header {
      background: #f8f9fd;
      padding: 14px 20px;
      border-bottom: 1px solid #e8e8e8;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .doc-title {
        display: flex;
        align-items: center;
        gap: 10px;
        .anticon { font-size: 22px; color: #014c75; }
        h4 { margin: 0; font-size: 15px; font-weight: 600; color: #333; }
      }
    }

    .card-body {
      padding: 20px;

      .doc-preview {
        background: #f8f9fd;
        border: 2px dashed #e8e8e8;
        border-radius: 8px;
        height: 160px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover { border-color: #014c75; background: #f0f5ff; }

        .anticon { font-size: 42px; color: #bbb; margin-bottom: 10px; }
        p { color: #999; margin: 0; font-size: 13px; }
      }

      .doc-info {
        margin-bottom: 16px;

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 7px 0;
          border-bottom: 1px solid #f0f0f0;

          &:last-child { border-bottom: none; }

          .label { color: #999; font-size: 13px; }
          .value { color: #333; font-size: 13px; font-weight: 500; max-width: 200px; text-align: right; word-break: break-all; }
        }
      }

      .doc-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .ant-btn { flex: 1; min-width: 70px; }
      }
    }

    &.no-document {
      .doc-preview {
        border-color: #f0f0f0;
        background: #fafafa;
        cursor: default;
        &:hover { border-color: #f0f0f0; background: #fafafa; }
      }
    }
  }

  .extracted-data {
    margin-top: 14px;
    padding: 12px 14px;
    background: #f0f9ff;
    border: 1px solid #bae0ff;
    border-radius: 6px;

    .extracted-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 10px;
      .anticon { color: #1677ff; }
      span { font-size: 13px; font-weight: 600; color: #1677ff; }
    }

    .extracted-field {
      display: flex;
      gap: 8px;
      margin-bottom: 5px;
      font-size: 12px;
      .field-label { color: #777; min-width: 110px; flex-shrink: 0; }
      .field-value { color: #333; font-weight: 500; word-break: break-word; }
    }

    .confidence-tag { margin-top: 8px; }
  }
`;

const getConfidenceColor = (confidence) => {
  if (confidence === "High") return "success";
  if (confidence === "Medium") return "warning";
  return "error";
};

const getFileNameFromUrl = (url) => {
  if (!url) return null;
  const parts = url.split("/");
  return parts[parts.length - 1] || null;
};

const TalentDocuments = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDetails = location.state?.from === "details";

  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [extractingDoc, setExtractingDoc] = useState(null);
  const [extractedData, setExtractedData] = useState({});
  const [updatingVerification, setUpdatingVerification] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState("");
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [thumbnailErrors, setThumbnailErrors] = useState({});

  const handleGoBack = () => {
    if (fromDetails) {
      navigate(`/admin/talent-profiles/details/${talentId}`);
    } else {
      navigate("/admin/talent-profiles");
    }
  };

  const fetchTalentProfile = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_TALENT_PROFILE}/${talentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.profile) {
        const profile = response.data.profile;
        setTalent(profile);
        setVerificationNotes(profile.idVerificationNotes || "");
        setAadhaarNumber(profile.aadhaarNumber || "");
        setPanNumber(profile.panNumber || "");
        setPassportNumber(profile.passportNumber || "");

        setDocuments([
          {
            type: "resume",
            typeName: "Resume",
            url: profile.resumeUrl || null,
            fileName: getFileNameFromUrl(profile.resumeUrl),
          },
          {
            type: "aadhaar",
            typeName: "Aadhaar Card",
            url: profile.aadhaarUrl || null,
            fileName: getFileNameFromUrl(profile.aadhaarUrl),
          },
          {
            type: "pan",
            typeName: "PAN Card",
            url: profile.panUrl || null,
            fileName: getFileNameFromUrl(profile.panUrl),
          },
          {
            type: "degree",
            typeName: "Degree Certificate",
            url: profile.degreeUrl || null,
            fileName: getFileNameFromUrl(profile.degreeUrl),
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching talent profile:", error);
      message.error("Failed to load talent profile");
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  useEffect(() => {
    if (talentId) {
      fetchTalentProfile();
    }
  }, [talentId, fetchTalentProfile]);

  useEffect(() => {
    if (!documents.some((d) => d.url)) return;
    const loadThumbnails = async () => {
      const token = localStorage.getItem("authToken");
      const urls = {};
      for (const doc of documents) {
        if (doc.url) {
          try {
            const response = await axios.post(
              API_CONST.GET_DOCUMENT_URL,
              { objectPath: doc.url },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            urls[doc.type] = response.data.downloadUrl;
          } catch (e) {
            // skip failed thumbnails silently
          }
        }
      }
      setThumbnailUrls(urls);
    };
    loadThumbnails();
  }, [documents]);

  const getDocIdNumber = (type) => {
    if (type === "aadhaar") return aadhaarNumber;
    if (type === "pan") return panNumber;
    if (type === "passport") return passportNumber;
    return null;
  };

  const getDocIdLabel = (type) => {
    if (type === "aadhaar") return "Aadhaar Number";
    if (type === "pan") return "PAN Number";
    if (type === "passport") return "Passport Number";
    return null;
  };

  const getDocumentDownloadUrl = async (objectPath) => {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(
      API_CONST.GET_DOCUMENT_URL,
      { objectPath },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.downloadUrl;
  };

  const handleView = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      setPreviewUrl(downloadUrl);
      setPreviewTitle(doc.typeName);
      setPreviewVisible(true);
    } catch {
      message.error("Failed to load document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      window.open(downloadUrl, "_blank");
    } catch {
      message.error("Failed to download document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleExtractData = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded for this slot"); return; }
    setExtractingDoc(doc.type);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.EXTRACT_TALENT_DOCUMENT,
        { talentId, documentType: doc.type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const extracted = response.data.extracted;
      setExtractedData((prev) => ({ ...prev, [doc.type]: extracted }));
      if (extracted?.idNumber) {
        if (doc.type === "aadhaar") setAadhaarNumber(extracted.idNumber);
        else if (doc.type === "pan") setPanNumber(extracted.idNumber);
      }
      message.success(`Data extracted from ${doc.typeName}`);
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to extract data from ${doc.typeName}`);
    } finally {
      setExtractingDoc(null);
    }
  };

  const openVerificationModal = (status) => {
    setPendingStatus(status);
    setNotesModalVisible(true);
  };

  const handleUpdateVerification = async () => {
    if (!pendingStatus) return;
    setUpdatingVerification(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.VERIFY_TALENT}/${talentId}`,
        {
          idVerificationStatus: pendingStatus,
          aadhaarNumber: aadhaarNumber || undefined,
          panNumber: panNumber || undefined,
          passportNumber: passportNumber || undefined,
          idVerificationNotes: verificationNotes || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Talent ID verification marked as ${pendingStatus}`);
      setNotesModalVisible(false);
      await fetchTalentProfile();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update verification status");
    } finally {
      setUpdatingVerification(false);
    }
  };

  const getVerificationBannerClass = () => {
    if (!talent) return "pending";
    if (talent.idVerificationStatus === "Verified") return "verified";
    if (talent.idVerificationStatus === "Failed") return "rejected";
    return "pending";
  };

  const getVerificationIcon = () => {
    if (!talent) return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    if (talent.idVerificationStatus === "Verified")
      return <SafetyCertificateOutlined style={{ color: "#52c41a" }} />;
    if (talent.idVerificationStatus === "Failed")
      return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
    return <ClockCircleOutlined style={{ color: "#faad14" }} />;
  };

  const getVerificationTitle = () => {
    if (!talent) return "Verification Pending";
    if (talent.idVerificationStatus === "Verified") return "ID Verification Passed";
    if (talent.idVerificationStatus === "Failed") return "ID Verification Failed";
    return "ID Verification Pending";
  };

  const getVerificationSubtext = () => {
    if (!talent) return "No verification decision has been made yet.";
    if (talent.idVerificationStatus === "Verified") {
      const date = talent.verifiedAt
        ? new Date(talent.verifiedAt).toLocaleDateString("en-IN")
        : null;
      return date
        ? `Verified on ${date}. ${talent.idVerificationNotes || ""}`
        : `All documents reviewed and approved. ${talent.idVerificationNotes || ""}`;
    }
    if (talent.idVerificationStatus === "Failed") {
      return talent.idVerificationNotes || "Documents did not pass verification review.";
    }
    return talent.idVerificationNotes
      ? `Notes: ${talent.idVerificationNotes}`
      : "Use the AI extraction buttons on each document card to review documents, then set the verification status.";
  };

  if (loading) {
    return (
      <TalentDocumentsWrapper>
        <div style={{ padding: "100px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
          <p style={{ marginTop: 16, color: "#666" }}>Loading documents...</p>
        </div>
      </TalentDocumentsWrapper>
    );
  }

  if (!talent) {
    return (
      <TalentDocumentsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Empty description="Talent not found" />
          <Button type="primary" onClick={handleGoBack} style={{ marginTop: 16 }}>
            Go Back
          </Button>
        </div>
      </TalentDocumentsWrapper>
    );
  }

  return (
    <TalentDocumentsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
          className="back-button"
        >
          {fromDetails ? "Back to Profile" : "Back to Talent Profiles"}
        </Button>
      </div>

      <h2>Talent Documents & ID Verification</h2>

      <div className="talent-info">
        <div className="info-left">
          <h3>{talent.name}</h3>
          <p>
            {talent.role} &bull; {talent.partnerOrganization}
          </p>
        </div>
        <Tag color="blue">Talent ID: {talentId}</Tag>
      </div>

      <div className={`verification-banner ${getVerificationBannerClass()}`}>
        <div className="banner-left">
          <div className="banner-icon">{getVerificationIcon()}</div>
          <div className="banner-text">
            <h4>{getVerificationTitle()}</h4>
            <p>{getVerificationSubtext()}</p>
          </div>
        </div>
        <div className="banner-actions">
          {talent.idVerificationStatus !== "Verified" && (
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              onClick={() => openVerificationModal("Verified")}
            >
              Mark as Verified
            </Button>
          )}
          {talent.idVerificationStatus !== "Failed" && (
            <Button
              danger
              icon={<CloseCircleFilled />}
              onClick={() => openVerificationModal("Failed")}
            >
              Mark as Failed
            </Button>
          )}
          {talent.idVerificationStatus !== "Pending" && (
            <Button icon={<ClockCircleOutlined />} onClick={() => openVerificationModal("Pending")}>
              Reset to Pending
            </Button>
          )}
          <Tooltip title="Add or edit verification notes and ID numbers">
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setPendingStatus(talent.idVerificationStatus || "Pending");
                setNotesModalVisible(true);
              }}
            >
              Edit Details
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="documents-grid">
        {documents.map((doc, index) => (
          <Card
            key={index}
            className={`document-card ${!doc.url ? "no-document" : ""}`}
            bodyStyle={{ padding: 0 }}
          >
            <div className="card-header">
              <div className="doc-title">
                <FilePdfOutlined />
                <h4>{doc.typeName}</h4>
              </div>
              {doc.url ? (
                <Tag icon={<CheckCircleFilled />} color="success">Uploaded</Tag>
              ) : (
                <Tag icon={<CloseCircleFilled />} color="default">Not Uploaded</Tag>
              )}
            </div>

            <div className="card-body">
              <div className="doc-preview" onClick={() => doc.url && handleView(doc)}>
                {loadingDoc === doc.type ? (
                  <LoadingOutlined style={{ fontSize: 42, color: "#014c75" }} spin />
                ) : thumbnailUrls[doc.type] && !thumbnailErrors[doc.type] ? (
                  <img
                    src={thumbnailUrls[doc.type]}
                    alt={doc.typeName}
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }}
                    onError={() => setThumbnailErrors((prev) => ({ ...prev, [doc.type]: true }))}
                  />
                ) : thumbnailUrls[doc.type] && thumbnailErrors[doc.type] ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <FilePdfOutlined style={{ fontSize: 42, color: "#ff4d4f", marginBottom: 8 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#ff4d4f", letterSpacing: 1 }}>PDF</span>
                    <p style={{ color: "#999", margin: "6px 0 0", fontSize: 13 }}>Click to preview</p>
                  </div>
                ) : (
                  <>
                    <FilePdfOutlined />
                    <p>{doc.url ? "Click to preview" : "No document uploaded"}</p>
                  </>
                )}
              </div>

              <div className="doc-info">
                <div className="info-row">
                  <span className="label">File Name</span>
                  <span className="value" title={doc.fileName}>
                    {doc.fileName
                      ? doc.fileName.length > 28
                        ? doc.fileName.substring(0, 28) + "..."
                        : doc.fileName
                      : "-"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Status</span>
                  <span className="value">{doc.url ? "Available" : "Not Uploaded"}</span>
                </div>
                {getDocIdLabel(doc.type) && (
                  <div className="info-row">
                    <span className="label">{getDocIdLabel(doc.type)}</span>
                    <Tooltip title={!getDocIdNumber(doc.type) ? "Use AI extraction or click 'Edit Details' to add manually" : ""}>
                      <span
                        className="value"
                        style={{
                          color: getDocIdNumber(doc.type) ? "#014c75" : "#bbb",
                          fontWeight: getDocIdNumber(doc.type) ? 600 : 400,
                          fontFamily: getDocIdNumber(doc.type) ? "monospace" : "inherit",
                          letterSpacing: getDocIdNumber(doc.type) ? "0.5px" : "normal",
                        }}
                      >
                        {getDocIdNumber(doc.type) || "Not extracted"}
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>

              {extractedData[doc.type] && (
                <div className="extracted-data">
                  <div className="extracted-header">
                    <RobotOutlined />
                    <span>AI Extracted Data</span>
                  </div>
                  {extractedData[doc.type].name && (
                    <div className="extracted-field">
                      <span className="field-label">Name:</span>
                      <span className="field-value">{extractedData[doc.type].name}</span>
                    </div>
                  )}
                  {extractedData[doc.type].idNumber && (
                    <div className="extracted-field">
                      <span className="field-label">ID Number:</span>
                      <span className="field-value">{extractedData[doc.type].idNumber}</span>
                    </div>
                  )}
                  {extractedData[doc.type].dateOfBirth && (
                    <div className="extracted-field">
                      <span className="field-label">Date of Birth:</span>
                      <span className="field-value">{extractedData[doc.type].dateOfBirth}</span>
                    </div>
                  )}
                  {extractedData[doc.type].address && (
                    <div className="extracted-field">
                      <span className="field-label">Address:</span>
                      <span className="field-value">{extractedData[doc.type].address}</span>
                    </div>
                  )}
                  {extractedData[doc.type].issuingAuthority && (
                    <div className="extracted-field">
                      <span className="field-label">Issued By:</span>
                      <span className="field-value">{extractedData[doc.type].issuingAuthority}</span>
                    </div>
                  )}
                  {extractedData[doc.type].expiryDate && (
                    <div className="extracted-field">
                      <span className="field-label">Expiry:</span>
                      <span className="field-value">{extractedData[doc.type].expiryDate}</span>
                    </div>
                  )}
                  {extractedData[doc.type].additionalInfo && (
                    <div className="extracted-field">
                      <span className="field-label">Other Info:</span>
                      <span className="field-value">{extractedData[doc.type].additionalInfo}</span>
                    </div>
                  )}
                  <div className="confidence-tag">
                    <Tag color={getConfidenceColor(extractedData[doc.type].extractionConfidence)}>
                      {extractedData[doc.type].extractionConfidence} Confidence
                    </Tag>
                  </div>
                </div>
              )}

              <div className="doc-actions" style={{ marginTop: 14 }}>
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleView(doc)}
                  disabled={!doc.url}
                  loading={loadingDoc === doc.type}
                >
                  View
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  type="primary"
                  style={doc.url ? { background: "#00d9a9", borderColor: "#00d9a9" } : {}}
                  onClick={() => handleDownload(doc)}
                  disabled={!doc.url}
                  loading={loadingDoc === doc.type}
                >
                  Download
                </Button>
                {doc.type !== "resume" && (
                  <Tooltip title="Use AI to read and extract identity data from this document">
                    <Button
                      icon={<RobotOutlined />}
                      onClick={() => handleExtractData(doc)}
                      disabled={!doc.url || extractingDoc === doc.type}
                      loading={extractingDoc === doc.type}
                      style={doc.url ? { borderColor: "#1677ff", color: "#1677ff" } : {}}
                    >
                      {extractedData[doc.type] ? "Re-extract" : "Extract via AI"}
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={900}
        centered
      >
        <iframe
          src={previewUrl}
          style={{ width: "100%", height: "70vh", border: "none" }}
          title={previewTitle}
        />
      </Modal>

      <Modal
        title={
          <span>
            <SafetyCertificateOutlined style={{ marginRight: 8, color: "#014c75" }} />
            Update ID Verification Status
          </span>
        }
        open={notesModalVisible}
        onCancel={() => setNotesModalVisible(false)}
        onOk={handleUpdateVerification}
        okText="Save"
        confirmLoading={updatingVerification}
        okButtonProps={{ style: { background: "#014c75", borderColor: "#014c75" } }}
        width={560}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              pendingStatus === "Verified"
                ? "You are marking this talent as ID Verified. This confirms all identity documents have been reviewed and approved."
                : pendingStatus === "Failed"
                ? "You are marking this talent as ID Verification Failed. The documents did not pass the review."
                : "You are resetting the verification status to Pending."
            }
            type={
              pendingStatus === "Verified"
                ? "success"
                : pendingStatus === "Failed"
                ? "error"
                : "warning"
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              Aadhaar Number (optional)
            </Text>
            <Input
              placeholder="e.g. 1234 5678 9012"
              value={aadhaarNumber}
              onChange={(e) => setAadhaarNumber(e.target.value)}
              maxLength={20}
            />
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              PAN Number (optional)
            </Text>
            <Input
              placeholder="e.g. ABCDE1234F"
              value={panNumber}
              onChange={(e) => setPanNumber(e.target.value)}
              maxLength={20}
            />
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              Passport Number (optional)
            </Text>
            <Input
              placeholder="e.g. A1234567"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              maxLength={40}
            />
          </div>
          <div>
            <Text strong style={{ display: "block", marginBottom: 6 }}>
              Verification Notes (optional)
            </Text>
            <TextArea
              rows={3}
              placeholder="Add notes about the verification decision, e.g., documents reviewed, discrepancies found, reason for rejection, etc."
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </TalentDocumentsWrapper>
  );
};

export default TalentDocuments;
